import {
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  styled,
  Typography,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  getGetScreeningCasesQueryKey,
  ScreeningClientType,
  ScreeningDesiredTest,
  useCreateScreeningCase,
} from '../../../api/created/screening.ts';
import { AddressContactDto, AddressDto, AddressDtoAddressType } from '../../../api/generated/model';
import { FormActionButtons } from '../../../components/form/FormActionButtons.tsx';
import { GermanDateInput } from '../../../components/form/GermanDateInput.tsx';
import { InstitutionContactFields } from '../../../components/form/InstitutionContactFields.tsx';

interface FormValues {
  clientType: ScreeningClientType | null;
  institution: AddressDto | null;
  contactPerson: AddressContactDto | null;
  groupName: string;
  desiredTest: ScreeningDesiredTest | null;
  testDate: string;
}

const defaultValues: FormValues = {
  clientType: null,
  institution: null,
  contactPerson: null,
  groupName: '',
  desiredTest: null,
  testDate: '',
};

const clientTypeOptions = [
  { value: ScreeningClientType.BAZ_ZURICH, label: 'BAZ Zürich' },
  { value: ScreeningClientType.SELF_REFERRAL, label: 'Selbstmelder' },
  { value: ScreeningClientType.OTHER, label: 'Andere' },
];

const desiredTestOptions = [
  { value: ScreeningDesiredTest.MX, label: 'Mx' },
  { value: ScreeningDesiredTest.QFT, label: 'QFT' },
  { value: ScreeningDesiredTest.RX, label: 'Rx' },
];

export function CreateScreeningDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    setValue,
    watch,
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues,
  });

  const clientType = watch('clientType');
  const showInstitutionFields = clientType === ScreeningClientType.OTHER;

  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
  }, [open, reset]);

  useEffect(() => {
    if (!showInstitutionFields) {
      setValue('institution', null);
      setValue('contactPerson', null);
    }
  }, [setValue, showInstitutionFields]);

  const createScreeningMutation = useCreateScreeningCase({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: getGetScreeningCasesQueryKey() });
        onClose();
      },
      onError: () => toast.error('Beim Erstellen des Screening-Falls ist ein Fehler aufgetreten'),
    },
  });

  const onSubmit = handleSubmit((data) => {
    if (!data.clientType) return;

    createScreeningMutation.mutate({
      screeningAssignment: null,
      clientType: data.clientType,
      institution: showInstitutionFields ? data.institution : null,
      contactPerson: showInstitutionFields ? data.contactPerson : null,
      groupName: data.groupName.trim() || null,
      desiredTest: data.desiredTest,
      testDate: data.testDate || null,
      assignedEmployeeId: null,
    });
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog sx={{ maxHeight: '90vh', overflowY: 'auto', overflowX: 'hidden' }}>
        <ModalClose />
        <Typography level="h4">Screening-Auftrag</Typography>

        <form onSubmit={onSubmit}>
          <Stack spacing={2}>
            <FormGrid>
              <FormControl error={!!errors.clientType}>
                <FormLabel>
                  <Typography>
                    Auftraggeber <RequiredMark>*</RequiredMark>
                  </Typography>
                </FormLabel>
                <Controller
                  name="clientType"
                  control={control}
                  rules={{ required: 'Auftraggeber muss ausgewählt werden' }}
                  render={({ field: { value, onChange } }) => (
                    <Select value={value} onChange={(_, value) => onChange(value)} placeholder="Bitte auswählen">
                      {clientTypeOptions.map((option) => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  )}
                />
                {errors.clientType && <FormHelperText>{errors.clientType.message}</FormHelperText>}
              </FormControl>

              {showInstitutionFields && (
                <InstitutionContactFields
                  formMethods={{ control, watch, setValue }}
                  layout="fragment"
                  addressField="institution"
                  contactField="contactPerson"
                  addressLabel="Institution"
                  contactLabel="Ansprechperson"
                  addressTypes={Object.values(AddressDtoAddressType)}
                  canChangeAddressType={true}
                  clearContactOnAddressChange="always"
                  contactClearOptions={false}
                  useAllContactsList={true}
                />
              )}

              <FormControl>
                <FormLabel>
                  <Typography>Gruppenbezeichnung</Typography>
                </FormLabel>
                <Input type="text" {...register('groupName')} />
              </FormControl>

              <FormControl>
                <FormLabel>
                  <Typography>Gewünschter Test</Typography>
                </FormLabel>
                <Controller
                  name="desiredTest"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Select value={value} onChange={(_, value) => onChange(value)} placeholder="Bitte auswählen">
                      {desiredTestOptions.map((option) => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>

              <FormControl>
                <FormLabel>
                  <Typography>Testtermin</Typography>
                </FormLabel>
                <GermanDateInput control={control} name="testDate" />
              </FormControl>
            </FormGrid>

            <FormActionButtons
              onAbortClick={handleClose}
              disableSubmit={!isValid}
              isLoading={createScreeningMutation.isPending}
            />
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}

const FormGrid = styled('div')`
  width: 700px;
  display: grid;
  gap: 0.75rem 1rem;
  grid-template-columns: minmax(0, 1fr);
  align-items: start;
`;

const RequiredMark = styled('span')`
  color: var(--joy-palette-danger-500);
`;
